import React, { useState } from "react";
import {
  Box,
  Image,
  Text,
  Badge,
  Flex,
  Icon,
  VStack,
  Collapse,
  Skeleton,
  Button,
  useColorModeValue,
  Divider,
} from "@chakra-ui/react";
import { FaStar, FaMapMarkerAlt, FaClock, FaChevronDown, FaChevronUp, FaPalette } from "react-icons/fa";
import PhotographerPreviewModal from "./PhotographerPreviewModal";
import { FaCamera, FaUserAlt, FaBuilding } from 'react-icons/fa';

const getUserTypeIcon = (userType) => {
  switch (userType) {
    case 'photographer':
      return FaCamera;
    case 'videographer':
      return FaCamera; 
    case 'model':
      return FaUserAlt;
    case 'studio':
      return FaBuilding;
    default:
      return FaUserAlt; // fallback
  }
};

function convertToAmPm(timeString) {
  const [hours, minutes] = timeString.split(':').map(Number);
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

const PhotographerList = ({ photographers }) => {
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const bgColor = useColorModeValue("white", "black");
  const textColor = useColorModeValue("gray.800", "white");
  const cardShadow = useColorModeValue("md", "dark-lg");

  const titleCase = (str) =>
    str.toLowerCase().split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");

  return (
    <VStack spacing={4} align="stretch">
      {photographers.map((photographer) => (
        <PhotographerCard
          key={photographer.id}
          photographer={photographer}
          onClick={() => setSelectedPhotographer(photographer)}
          bgColor={bgColor}
          textColor={textColor}
          cardShadow={cardShadow}
          titleCase={titleCase}
        />
      ))}

      <PhotographerPreviewModal
        isOpen={!!selectedPhotographer}
        onClose={() => setSelectedPhotographer(null)}
        creator={selectedPhotographer}
      />
    </VStack>
  );
};

const PhotographerCard = ({ photographer, onClick, bgColor, textColor, cardShadow, titleCase }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      shadow={cardShadow}
      overflow="hidden"
      p={3}
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      gap={4}
      onClick={onClick}
      _hover={{ boxShadow: "xl", cursor: "pointer" }}
    >
      {/* Avatar with Skeleton */}
      <Skeleton isLoaded={imageLoaded} borderRadius="md">
        <Image
          src={photographer.avatar || "/placeholder.jpg"}
          alt={photographer.name}
          objectFit="cover"
          loading="lazy"
          borderRadius="md"
          boxSize="100px"
          maxW="100px"
          maxH="140px"
          onLoad={() => setImageLoaded(true)}
        />
      </Skeleton>

      {/* Info */}
      <Box flex="1" color={textColor}>
        <Text fontSize="lg" fontWeight="bold">
          {photographer.name}
        </Text>

        <Flex align="center" mt={1}>
          <Icon as={getUserTypeIcon(photographer.user_type)} color="purple.400" mr={1} />
          <Text fontSize="sm">{titleCase(photographer.user_type) || "Unknown"}</Text>
        </Flex>

        <Flex align="center" mt={1}>
          <Icon as={FaPalette} color="purple.400" mr={1} />
          <Text fontSize="sm">{photographer.photography_style || "Unknown"}</Text>
        </Flex>

        <Flex align="center" mt={1}>
          <Icon as={FaClock} color="green.400" mr={1} />
          <Text fontSize="sm">
            {convertToAmPm(photographer.start_time)} - {convertToAmPm(photographer.end_time)}
          </Text>
        </Flex>

        <Flex align="center" justify="space-between" mt={2}>
          <Badge colorScheme="purple" fontSize="sm">
            ${photographer.price}/hr
          </Badge>
          <Button size="xs" colorScheme="purple" borderRadius="full">
            View
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

export default PhotographerList;